/**
 * Shared LTR-track horizontal carousel (RTL-safe: callers force direction:ltr on the track).
 * Use <daddl-carousel> wrapping track + prev/next, or window.daddlBindCarousel(opts).
 * data-coverflow: scale the card closest to the track center.
 * data-loop: next/prev wrap by rotating DOM (not used by featured tabs).
 *
 * Centered peek loop: window.daddlBindCenteredLoop (featured tabs + video trust).
 * 2026 pattern: duplicate one full period before and after, translate the
 * track, then rebase by exactly one period with transition none. Ignore
 * bubbled child transitionend (slides often animate their own transform).
 */
(function () {
  'use strict';

  function daddlBindCarousel(opts) {
    const track = opts.track;
    const prev = opts.prev;
    const next = opts.next;
    if (!track || !prev || !next) return;

    const itemSelector = opts.itemSelector || ':scope > *';
    const stepItems = opts.stepItems || 1;
    const nav = opts.nav || null;
    const coverflow = !!opts.coverflow;
    const loop = !!opts.loop;
    let rotating = false;

    function items() {
      return track.querySelectorAll(itemSelector);
    }

    function itemStep() {
      const item = track.querySelector(itemSelector);
      if (!item) return 240;
      const styles = getComputedStyle(track);
      const gap = parseFloat(styles.columnGap || styles.gap) || 0;
      return (item.offsetWidth + gap) * stepItems;
    }

    function needsScroll() {
      return track.scrollWidth > track.clientWidth + 8;
    }

    function maxScroll() {
      return Math.max(0, track.scrollWidth - track.clientWidth);
    }

    function atStart() {
      return track.scrollLeft <= 4;
    }

    function atEnd() {
      return track.scrollLeft >= maxScroll() - 4;
    }

    function updateCoverflow() {
      if (!coverflow) return;
      const list = items();
      if (!list.length) return;
      const mid = track.getBoundingClientRect().left + track.clientWidth / 2;
      let best = list[0];
      let bestDist = Infinity;
      list.forEach((el) => {
        const rect = el.getBoundingClientRect();
        const dist = Math.abs(rect.left + rect.width / 2 - mid);
        if (dist < bestDist) {
          bestDist = dist;
          best = el;
        }
      });
      list.forEach((el) => el.classList.toggle('is-center', el === best));
    }

    function update() {
      const show = needsScroll();
      if (nav) nav.classList.toggle('is-visible', show);
      if (loop) {
        prev.disabled = !show;
        next.disabled = !show;
      } else {
        prev.disabled = !show || atStart();
        next.disabled = !show || atEnd();
      }
      updateCoverflow();
    }

    function goNext() {
      const step = itemStep();
      const list = items();
      if (loop && list.length > 1 && atEnd()) {
        rotating = true;
        track.appendChild(list[0]);
        track.scrollLeft -= step;
        rotating = false;
      }
      track.scrollBy({ left: step, behavior: 'smooth' });
    }

    function goPrev() {
      const step = itemStep();
      const list = items();
      if (loop && list.length > 1 && atStart()) {
        rotating = true;
        track.insertBefore(list[list.length - 1], track.firstElementChild);
        track.scrollLeft += step;
        rotating = false;
      }
      track.scrollBy({ left: -step, behavior: 'smooth' });
    }

    prev.addEventListener('click', goPrev);
    next.addEventListener('click', goNext);
    track.addEventListener(
      'scroll',
      () => {
        if (!rotating) update();
      },
      { passive: true }
    );
    window.addEventListener('resize', update);
    if (typeof ResizeObserver !== 'undefined') new ResizeObserver(update).observe(track);

    const currentSelector = opts.scrollCurrent;
    if (currentSelector) {
      const current = track.querySelector(currentSelector);
      if (current) {
        requestAnimationFrame(() => {
          current.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'instant' });
          update();
        });
      }
    }

    update();
    return { update };
  }

  /**
   * Infinite centered-peek track (clone strip + period rebase).
   * Viewport clips; track is a flex row with direction:ltr.
   *
   * @param {object} opts
   * @param {HTMLElement} opts.viewport
   * @param {HTMLElement} opts.track
   * @param {string} opts.slideSelector
   * @param {HTMLElement} [opts.prev]
   * @param {HTMLElement} [opts.next]
   * @param {string} [opts.cloneAttr]
   * @param {string} [opts.instantClass]
   * @param {string} [opts.ignoreDown]
   * @param {function(HTMLElement):void} [opts.prepareClone]
   * @param {function(number, HTMLElement[], object):void} [opts.onActive]
   * @param {function(number, number, HTMLElement[]):void} [opts.onRebase]
   * @param {function(HTMLElement, number):void} [opts.onTap]
   */
  function daddlBindCenteredLoop(opts) {
    const viewport = opts.viewport;
    const track = opts.track;
    if (!viewport || !track) return null;

    const slideSelector = opts.slideSelector || ':scope > *';
    const cloneAttr = opts.cloneAttr || 'data-loop-clone';
    const instantClass = opts.instantClass || 'is-instant';
    const ignoreDown = opts.ignoreDown || 'a, button';
    const prepareClone = opts.prepareClone || function () {};
    const onActive = opts.onActive || function () {};
    const onRebase = opts.onRebase || function () {};
    const onTap = opts.onTap || null;
    const TAP_SLOP = 8;
    const TRANSITION_MS = 500;

    const originals = Array.prototype.slice.call(track.querySelectorAll(slideSelector));
    const N = originals.length;
    if (!N) return null;

    const loop = N > 1;
    let activeIndex = 0;

    if (loop) {
      const before = document.createDocumentFragment();
      const after = document.createDocumentFragment();
      originals.forEach(function (el) {
        const a = el.cloneNode(true);
        const b = el.cloneNode(true);
        [a, b].forEach(function (clone) {
          clone.setAttribute(cloneAttr, '');
          clone.setAttribute('aria-hidden', 'true');
          clone.setAttribute('tabindex', '-1');
          clone.querySelectorAll('[id]').forEach(function (node) {
            node.removeAttribute('id');
          });
          prepareClone(clone);
        });
        before.appendChild(a);
        after.appendChild(b);
      });
      track.insertBefore(before, originals[0]);
      track.appendChild(after);
      activeIndex = N;
    }

    const slides = Array.prototype.slice.call(track.querySelectorAll(slideSelector));
    let moveToken = 0;
    let settleTimer = 0;

    function gapPx() {
      const styles = getComputedStyle(track);
      let raw = styles.columnGap;
      if (!raw || raw === 'normal') raw = styles.gap;
      const n = parseFloat(raw);
      return Number.isFinite(n) ? n : 0;
    }

    function metrics() {
      const sw = slides[0] ? slides[0].offsetWidth : 0;
      const gap = gapPx();
      return { sw: sw, gap: gap, step: sw + gap };
    }

    function offsetFor(index) {
      const m = metrics();
      return viewport.clientWidth / 2 - m.sw / 2 - index * m.step;
    }

    function rebaseIndex(index) {
      if (!loop) return index;
      while (index >= 2 * N) index -= N;
      while (index < N) index += N;
      return index;
    }

    function applyTransform(index, animate) {
      if (animate) {
        viewport.classList.remove(instantClass);
        track.style.transition = 'transform 0.5s cubic-bezier(0.22, 0.61, 0.36, 1)';
      } else {
        viewport.classList.add(instantClass);
        track.style.transition = 'none';
      }
      track.style.transform = 'translate3d(' + offsetFor(index) + 'px,0,0)';
      if (!animate) {
        void track.offsetWidth;
        requestAnimationFrame(function () {
          viewport.classList.remove(instantClass);
        });
      }
    }

    function settle() {
      if (!loop) return;
      const next = rebaseIndex(activeIndex);
      if (next === activeIndex) return;
      const from = activeIndex;
      activeIndex = next;
      onRebase(from, next, slides);
      applyTransform(activeIndex, false);
      onActive(activeIndex, slides, { instant: true, rebased: true });
    }

    function position(animate) {
      const token = ++moveToken;
      applyTransform(activeIndex, animate);
      onActive(activeIndex, slides, { instant: !animate, rebased: false });
      window.clearTimeout(settleTimer);
      if (!animate) {
        settle();
        return;
      }
      settleTimer = window.setTimeout(function () {
        if (token !== moveToken) return;
        settle();
      }, TRANSITION_MS + 40);
    }

    function onTrackTransitionEnd(event) {
      if (event.target !== track) return;
      if (event.propertyName && event.propertyName !== 'transform') return;
      window.clearTimeout(settleTimer);
      settle();
    }

    track.addEventListener('transitionend', onTrackTransitionEnd);

    function go(delta) {
      if (!delta) {
        position(true);
        return;
      }
      if (!loop) {
        const next = activeIndex + delta;
        if (next < 0 || next > N - 1) return;
        activeIndex = next;
      } else {
        activeIndex += delta;
        if (activeIndex < 0) activeIndex = 0;
        if (activeIndex > slides.length - 1) activeIndex = slides.length - 1;
      }
      position(true);
    }

    function setIndex(index, animate) {
      activeIndex = loop ? N + (((index % N) + N) % N) : index;
      position(!!animate);
    }

    function goTo(index, animate) {
      if (!loop) {
        if (index < 0 || index > N - 1) return;
        activeIndex = index;
      } else {
        if (index < 0) index = 0;
        if (index > slides.length - 1) index = slides.length - 1;
        activeIndex = index;
      }
      position(!!animate);
    }

    if (opts.prev) opts.prev.addEventListener('click', function () { go(-1); });
    if (opts.next) opts.next.addEventListener('click', function () { go(1); });

    let dragging = false;
    let startX = 0;
    let startY = 0;
    let startOff = 0;
    let moved = 0;
    let movedY = 0;
    let axis = null;
    let downSlide = null;
    let suppressClick = false;

    track.addEventListener('pointerdown', function (event) {
      if (event.button && event.button !== 0) return;
      if (event.target.closest(ignoreDown)) {
        dragging = false;
        downSlide = null;
        return;
      }
      dragging = true;
      axis = null;
      moved = 0;
      movedY = 0;
      startX = event.clientX;
      startY = event.clientY;
      startOff = offsetFor(activeIndex);
      downSlide = event.target.closest(slideSelector);
    });

    track.addEventListener('pointermove', function (event) {
      if (!dragging) return;
      moved = event.clientX - startX;
      movedY = event.clientY - startY;
      if (!axis) {
        if (Math.abs(moved) <= TAP_SLOP && Math.abs(movedY) <= TAP_SLOP) return;
        if (Math.abs(moved) > Math.abs(movedY)) {
          axis = 'x';
          moveToken += 1;
          window.clearTimeout(settleTimer);
          track.style.transition = 'none';
          if (track.setPointerCapture) track.setPointerCapture(event.pointerId);
        } else {
          axis = 'y';
          dragging = false;
        }
        return;
      }
      if (axis === 'x') {
        track.style.transform = 'translate3d(' + (startOff + moved) + 'px,0,0)';
      }
    });

    function finishGesture(cancelled) {
      if (!dragging) return;
      dragging = false;
      if (cancelled) {
        if (axis === 'x') position(true);
        axis = null;
        downSlide = null;
        return;
      }
      if (axis === 'x') {
        if (Math.abs(moved) > TAP_SLOP) suppressClick = true;
        const m = metrics();
        const threshold = Math.max(40, m.sw * 0.18);
        let steps = m.step ? Math.round(-moved / m.step) : 0;
        if (steps === 0 && Math.abs(moved) > threshold) {
          steps = moved < 0 ? 1 : -1;
        }
        if (steps) go(steps);
        else position(true);
      } else if (!axis && onTap && downSlide && Math.abs(moved) <= TAP_SLOP && Math.abs(movedY) <= TAP_SLOP) {
        onTap(downSlide, slides.indexOf(downSlide));
      }
      axis = null;
      downSlide = null;
    }

    track.addEventListener('click', function (event) {
      if (!suppressClick) return;
      event.preventDefault();
      event.stopPropagation();
      suppressClick = false;
    }, true);

    track.addEventListener('pointerup', function () { finishGesture(false); });
    track.addEventListener('pointercancel', function () { finishGesture(true); });

    function relayout() {
      position(false);
    }

    window.addEventListener('resize', relayout);
    if (typeof ResizeObserver === 'function') {
      new ResizeObserver(relayout).observe(viewport);
    }

    position(false);
    requestAnimationFrame(function () { position(false); });

    return {
      go: go,
      goTo: goTo,
      position: position,
      setIndex: setIndex,
      getIndex: function () { return activeIndex; },
      slides: slides,
      N: N,
      loop: loop,
      realIndex: function (domIndex) {
        return ((domIndex % N) + N) % N;
      }
    };
  }

  window.daddlBindCarousel = daddlBindCarousel;
  window.daddlBindCenteredLoop = daddlBindCenteredLoop;

  class DaddlCarousel extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      daddlBindCarousel({
        track: this.querySelector('[data-daddl-carousel-track]'),
        prev: this.querySelector('[data-daddl-carousel-prev]'),
        next: this.querySelector('[data-daddl-carousel-next]'),
        nav: this.querySelector('[data-daddl-carousel-nav]'),
        itemSelector: this.getAttribute('data-item-selector') || ':scope > *',
        stepItems: parseInt(this.getAttribute('data-step-items') || '1', 10),
        scrollCurrent: this.getAttribute('data-scroll-current'),
        coverflow: this.hasAttribute('data-coverflow'),
        loop: this.hasAttribute('data-loop')
      });
    }
  }

  if (!customElements.get('daddl-carousel')) {
    customElements.define('daddl-carousel', DaddlCarousel);
  }
})();
